import { Fragment, useState, type ComponentProps, type ReactNode } from 'react';
import { Icon } from './Icon.js';
import { TitleInfo } from './Info.js';

/**
 * Text input with a label, optional hint and error (error is announced). `hideLabel` keeps the label for screen
 * readers only, where a sheet title above already says it.
 */
export function TextField({
  id,
  label,
  hint,
  error,
  hideLabel = false,
  className,
  ...input
}: Omit<ComponentProps<'input'>, 'id'> & {
  id: string;
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
  hideLabel?: boolean;
}) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={className ? `field ${className}` : 'field'}>
      <label className={hideLabel ? 'visually-hidden' : 'field-label'} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="field-input"
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        {...input}
      />
      {hint && (
        <p id={hintId} className="field-hint">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * A date as a field (board "Neue Bohne": a field with the date as text, a calendar at its end). The native picker stays
 * (wheel or calendar on the phone), but iOS draws an empty date input as a flat dark bar without text, 27 px high and
 * past the right edge (Dominik 23.10.2026, "Urlaub"): the input gets the field's box (components.css .field-date), and
 * while it is empty `placeholder` stands over it ("Datum wählen").
 */
export function DateField({
  id,
  label,
  hint,
  error,
  hideLabel = false,
  placeholder,
  className,
  value,
  ...input
}: Omit<ComponentProps<'input'>, 'id' | 'type' | 'value'> & {
  id: string;
  label: string;
  value: string;
  hint?: ReactNode;
  error?: ReactNode;
  hideLabel?: boolean;
  placeholder?: string;
}) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const empty = value === '';
  return (
    <div
      className={['field', 'field-date', empty ? 'is-empty' : '', className ?? ''].filter(Boolean).join(' ')}
    >
      <label className={hideLabel ? 'visually-hidden' : 'field-label'} htmlFor={id}>
        {label}
      </label>
      <div className="date-box">
        <input
          id={id}
          type="date"
          className="field-input"
          value={value}
          aria-describedby={describedBy}
          aria-invalid={error ? true : undefined}
          {...input}
        />
        {empty && placeholder && (
          <span className="date-placeholder" aria-hidden="true">
            {placeholder}
          </span>
        )}
        <Icon name="calendar" size="sm" className="date-icon" />
      </div>
      {hint && (
        <p id={hintId} className="field-hint">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** A native select (keyboard, iOS wheel) styled like a field, with its own chevron; label as in TextField. */
export function Select({
  id,
  label,
  hideLabel = false,
  className,
  children,
  ...select
}: Omit<ComponentProps<'select'>, 'id'> & { id?: string; label: string; hideLabel?: boolean }) {
  return (
    <div className={className ? `field ${className}` : 'field'}>
      {id ? (
        <label className={hideLabel ? 'visually-hidden' : 'field-label'} htmlFor={id}>
          {label}
        </label>
      ) : null}
      <div className="select">
        <select id={id} className="field-input" aria-label={id ? undefined : label} {...select}>
          {children}
        </select>
        <Icon name="chevronDown" size="sm" className="select-icon" />
      </div>
    </div>
  );
}

/** Splits a code length into display groups: 8 -> 4+4, 6 -> 3+3, 7 -> 4+3. */
export function codeGroups(length: number): number[] {
  if (length <= 4) return [length];
  const first = Math.ceil(length / 2);
  return [first, length - first];
}

export type CodeCellsProps = Omit<ComponentProps<'input'>, 'id' | 'value' | 'onChange' | 'size'> & {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** number of significant characters per group, e.g. [4, 4] */
  groups: number[];
  /** significant characters of the raw input (digits, or upper-case letters and digits) */
  significant: (raw: string) => string;
  hint?: ReactNode;
  /** an explanation behind an (i) after the label (boards "Anmelden", "Maschine koppeln") */
  info?: ReactNode;
  infoLabel?: string;
};

/**
 * Code entry drawn as cells. It is ONE real input (paste, iOS one-time-code autofill, screen readers and tests
 * see a normal text field); the cells behind it only show what was typed.
 */
export function CodeCells({
  id,
  label,
  value,
  onChange,
  groups,
  significant,
  hint,
  info,
  infoLabel = '',
  className,
  onFocus,
  onBlur,
  ...input
}: CodeCellsProps) {
  const [focused, setFocused] = useState(false);
  const chars = significant(value);
  const total = groups.reduce((a, b) => a + b, 0);
  const hintId = hint ? `${id}-hint` : undefined;
  const offsets = groups.map((_, g) => groups.slice(0, g).reduce((a, b) => a + b, 0));
  return (
    <div className={className ? `code ${className}` : 'code'}>
      <TitleInfo info={info} label={infoLabel}>
        <label className="field-label" htmlFor={id}>
          {label}
        </label>
      </TitleInfo>
      <div className={`code-box code-${total > 6 ? 'long' : 'short'}`}>
        <div className="code-cells" aria-hidden="true">
          {groups.map((n, g) => (
            <Fragment key={g}>
              {g > 0 && <span className="code-sep">–</span>}
              {Array.from({ length: n }, (_, k) => {
                const i = (offsets[g] ?? 0) + k;
                const active = focused && i === Math.min(chars.length, total - 1);
                return (
                  <span key={i} className={active ? 'code-cell is-active' : 'code-cell'}>
                    {chars[i] ?? ''}
                    {active && i === chars.length && <span className="caret" />}
                  </span>
                );
              })}
            </Fragment>
          ))}
        </div>
        <input
          id={id}
          className="code-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={(e) => {
            setFocused(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            onBlur?.(e);
          }}
          aria-describedby={hintId}
          {...input}
        />
      </div>
      {hint && (
        <p id={hintId} className="field-hint">
          {hint}
        </p>
      )}
    </div>
  );
}

export function Checkbox({
  id,
  label,
  className,
  ...input
}: Omit<ComponentProps<'input'>, 'type' | 'id'> & { id: string; label: ReactNode }) {
  return (
    <label className={className ? `check ${className}` : 'check'} htmlFor={id}>
      <input id={id} type="checkbox" className="check-input" {...input} />
      <span className="check-box" aria-hidden="true" />
      <span className="check-label">{label}</span>
    </label>
  );
}

/**
 * An on/off setting as a Material 3 switch at the right of its row (prototype "Reiter Einstellungen"): the label and
 * its hint left, the switch right; the whole row is the target. A checkbox underneath (role switch), so forms, tests
 * and screen readers keep working. `info` puts the explanation behind an (i) after the label (boards
 * "Fortgeschritten", "Waage"); the switch is named by its label alone (aria-labelledby), not by the (i) in its row.
 */
export function Toggle({
  id,
  label,
  hint,
  info,
  infoLabel = '',
  infoTestId,
  hideLabel = false,
  className,
  ...input
}: Omit<ComponentProps<'input'>, 'type' | 'id'> & {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  /** the explanation behind the (i), with the (i)'s name for screen readers */
  info?: ReactNode;
  infoLabel?: string;
  infoTestId?: string;
  /** the switch alone (in a top bar, a sheet's header): the label only for screen readers */
  hideLabel?: boolean;
}) {
  const labelId = `${id}-label`;
  const hintId = hint ? `${id}-hint` : undefined;
  const text = (
    <span className="row-label" id={labelId}>
      {label}
    </span>
  );
  return (
    <label
      className={['toggle', hideLabel ? 'is-bare' : '', className ?? ''].filter(Boolean).join(' ')}
      htmlFor={id}
    >
      <span className={hideLabel ? 'visually-hidden' : 'row-text'}>
        {info && !hideLabel ? (
          <TitleInfo info={info} label={infoLabel} testId={infoTestId}>
            {text}
          </TitleInfo>
        ) : (
          text
        )}
        {hint && (
          <span className="hint" id={hintId}>
            {hint}
          </span>
        )}
      </span>
      <input
        id={id}
        type="checkbox"
        role="switch"
        className="toggle-input"
        aria-labelledby={labelId}
        aria-describedby={hintId}
        {...input}
      />
      <span className="toggle-track" aria-hidden="true" />
    </label>
  );
}

/**
 * A filter as a chip with its list (prototype "Verlauf": bean, period, taste in one row): the chosen option's text and a
 * chevron, the native select over it (transparent), so the chip is as wide as what it shows. `on` fills it while it
 * narrows the list.
 */
export function ChipSelect({
  id,
  label,
  text,
  on = false,
  className,
  children,
  ...select
}: Omit<ComponentProps<'select'>, 'id'> & { id: string; label: string; text: ReactNode; on?: boolean }) {
  return (
    <span className={['chip-select', on ? 'is-on' : '', className ?? ''].filter(Boolean).join(' ')}>
      <span className="chip-select-text" aria-hidden="true">
        {text}
      </span>
      <Icon name="chevronDown" size="sm" />
      <select id={id} className="chip-select-input" aria-label={label} {...select}>
        {children}
      </select>
    </span>
  );
}

/**
 * A choice as the value at the end of its row (boards "System": "Protokoll · INFO ⌄", "Eine Schnellwahl": "Bohne ·
 * nicht ändern ⌄"): the chosen option's text in the dim value type and a chevron, the native select over it
 * (transparent: the iOS wheel, the keyboard), so the row stays one line.
 */
export function LinkSelect({
  id,
  label,
  text,
  className,
  children,
  ...select
}: Omit<ComponentProps<'select'>, 'id'> & { id?: string; label: string; text: ReactNode }) {
  return (
    <span className={className ? `link-select ${className}` : 'link-select'}>
      <span className="link-select-text" aria-hidden="true">
        {text}
      </span>
      <Icon name="chevronDown" size="sm" />
      <select id={id} className="link-select-input" aria-label={label} {...select}>
        {children}
      </select>
    </span>
  );
}
